<template>
  <div class="px-6 overflow-auto h-[55vh]">

    <!-- ── Facility Header ─────────────────────────────────────────────────── -->
    <!-- Sits at the very top of the invoice, above all patient/billing info.  -->
    <!-- The facility object arrives via billDetails.patient.facility which    -->
    <!-- the billing controller already eager-loads in bill#show.              -->
    <div class="text-center pb-4 mb-5 border-b border-gray-200">
      <!-- Logo — centred, own line, doubled from h-14 to h-28 -->
      <div class="flex justify-center mb-3">
        <img
          v-if="facility.logo_url"
          :src="facility.logo_url"
          :alt="facility.FacilityName"
          class="h-28 w-auto max-w-[220px] object-contain"
        />
        <div
          v-else
          class="h-28 w-28 rounded bg-gray-100 border border-gray-200 flex items-center justify-center"
        >
          <svg class="w-14 h-14 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
              d="M3 21V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14M3 21h18M9 21V11h6v10"/>
          </svg>
        </div>
      </div>

      <!-- Facility details — centred below the logo -->
      <h2 class="text-base font-bold text-gray-900 leading-snug">
        {{ facility.FacilityName || "—" }}
      </h2>
      <p v-if="facilityLocation" class="text-xs text-gray-500 mt-0.5">{{ facilityLocation }}</p>
      <p v-if="facility.e_tims_no && facility.e_tims_no !== 'N/A'" class="text-xs text-gray-400 mt-0.5">
        eTIMS: {{ facility.e_tims_no }}
      </p>

      <!-- Invoice number + date — below facility details, still centred -->
      <div class="mt-2">
        <p class="text-xs text-gray-500 font-medium">
          Invoice No. <span class="font-bold text-gray-800">{{ billDetails.bill_number || "—" }}</span>
          &nbsp;·&nbsp;
          <span class="text-gray-400">{{ formatDate(billDetails.created_at) }}</span>
        </p>
      </div>
    </div>
    <!-- ── /Facility Header ────────────────────────────────────────────────── -->

    <!-- Display FFS Sum Amount -->
    <div v-if="hasFfsItems" class="mb-4">
      <p class="text-sm font-semibold text-gray-600">Total FFS Coverage Applied:</p>
      <p class="text-sm">{{ formatCurrency(ffsTotalAmount) }}</p>
    </div>

    <!-- Patient details — Cash bill -->
    <div v-if="!isInsuranceBill" class="grid grid-cols-2 gap-4 mb-4">
      <div>
        <p class="text-sm font-semibold text-gray-600">Patient Name:</p>
        <p class="text-sm">{{ billDetails.patient?.FullName }}</p>
      </div>
      <div>
        <p class="text-sm font-semibold flex justify-end text-gray-600">Patient Number:</p>
        <p class="text-sm flex justify-end">{{ billDetails.patient?.PhoneNumber }}</p>
      </div>
    </div>

    <!-- Patient details — Insurance bill -->
    <div v-else class="grid grid-cols-2 gap-4 mb-4">
      <div>
        <p class="text-sm font-semibold text-gray-600">Patient Reg. No.:</p>
        <p class="text-sm">{{ billDetails.patient?.unique_code || "N/A" }}</p>
      </div>
      <div>
        <p class="text-sm font-semibold flex justify-end text-gray-600">Invoice Number:</p>
        <p class="text-sm flex justify-end">{{ billDetails.bill_number }}</p>
      </div>
      <div>
        <p class="text-sm font-semibold text-gray-600">Patient Name:</p>
        <p class="text-sm">{{ billDetails.patient?.FullName || "N/A" }}</p>
      </div>
      <div>
        <p class="text-sm font-semibold flex justify-end text-gray-600">Patient Number:</p>
        <p class="text-sm flex justify-end">{{ billDetails.patient?.PhoneNumber }}</p>
      </div>
      <div>
        <p class="text-sm font-semibold text-gray-600">Invoice Date:</p>
        <p class="text-sm">{{ billDetails.created_at || "N/A" }}</p>
      </div>
      <div>
        <p class="text-sm font-semibold flex justify-end text-gray-600">Visit No:</p>
        <p class="text-sm flex justify-end">{{ billDetails.visit?.VisitCode || "N/A" }}</p>
      </div>
      <div>
        <p class="text-sm font-semibold text-gray-600">Insurance:</p>
        <p class="text-sm">{{ billDetails.patient_scheme?.scheme?.insurance?.ProviderName }}</p>
      </div>
      <div>
        <p class="text-sm font-semibold flex justify-end text-gray-600">Scheme:</p>
        <p class="text-sm flex justify-end">{{ billDetails.patient_scheme?.scheme?.name }}</p>
      </div>
    </div>

    <!-- Bill items table -->
    <div class="w-full mt-5">
      <table class="w-full text-left text-sm">
        <thead class="border-b border-gray-300 text-gray-900">
          <tr>
            <th class="py-1 pl-4 pr-3 text-left font-semibold text-gray-900 sm:pl-0">Service</th>
            <th class="hidden px-4 py-1 text-right font-semibold text-gray-900 sm:table-cell">Units</th>
            <th class="hidden px-4 py-1 text-right font-semibold text-gray-900 sm:table-cell">Unit Price</th>
            <th class="px-4 py-1 text-right font-semibold text-gray-900 sm:table-cell">Amount</th>
            <th class="px-4 py-1 text-right font-semibold text-gray-900 sm:table-cell">Paid Via</th>
            <th v-if="isShaBill" class="px-4 py-1 text-right font-semibold text-gray-900 sm:table-cell">SHA Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="procedureItems.length">
            <td :colspan="columnCount" class="font-bold pt-3 pb-1">Procedure</td>
          </tr>
          <tr
            v-for="(billItem, index) in procedureItems"
            :key="'proc-' + index"
            class="border-b border-gray-200"
          >
            <td class="py-5 pl-4 pr-3 text-gray-900 sm:pl-0">
              <div class="font-medium">{{ billItem.facility_service?.name }}</div>
            </td>
            <td class="hidden px-4 py-1 text-right text-gray-500 sm:table-cell">{{ billItem.units }}</td>
            <td class="hidden px-4 py-1 text-right text-gray-500 sm:table-cell">{{ formatCurrency(billItem.unit_price) }}</td>
            <td class="hidden px-4 py-1 text-right text-gray-500 sm:table-cell">{{ formatCurrency(billItem.total_amount) }}</td>
            <td class="hidden px-4 py-1 text-right text-gray-500 sm:table-cell">{{ billItem.paid_via || 'Not Paid' }}</td>
            <td v-if="isShaBill" class="hidden px-4 py-1 text-right text-gray-500 sm:table-cell"><span v-if="billItem.sha_intervention_info?.submitted_to_sha">{{ shaStatusBadge(billItem) }}</span>
              <button
                v-else
                @click="resubmitIntervention(billItem.id)"
                :disabled="resubmitting[billItem.id]"
                class="px-2 py-1 bg-yellow-500 hover:bg-yellow-600 text-white text-xs rounded"
              >
                {{ resubmitting[billItem.id] ? 'Resubmitting...' : 'Resubmit' }}
              </button></td>
          </tr>

          <tr v-if="consultationItems.length">
            <td :colspan="columnCount" class="font-bold pt-3 pb-1">Consultation</td>
          </tr>
          <tr
            v-for="(billItem, index) in consultationItems"
            :key="'cons-' + index"
            class="border-b border-gray-200"
          >
            <td class="py-5 pl-4 pr-3 text-gray-900 sm:pl-0">
              <div class="font-medium">{{ billItem.facility_service?.name }}</div>
            </td>
            <td class="hidden px-4 py-1 text-right text-gray-500 sm:table-cell">{{ billItem.units }}</td>
            <td class="hidden px-4 py-1 text-right text-gray-500 sm:table-cell">{{ formatCurrency(billItem.unit_price) }}</td>
            <td class="hidden px-4 py-1 text-right text-gray-500 sm:table-cell">{{ formatCurrency(billItem.total_amount) }}</td>
            <td class="hidden px-4 py-1 text-right text-gray-500 sm:table-cell">{{ billItem.paid_via || 'Not Paid' }}</td>
            <td v-if="isShaBill" class="hidden px-4 py-1 text-right text-gray-500 sm:table-cell"><span v-if="billItem.sha_intervention_info?.submitted_to_sha">{{ shaStatusBadge(billItem) }}</span>
              <button
                v-else
                @click="resubmitIntervention(billItem.id)"
                :disabled="resubmitting[billItem.id]"
                class="px-2 py-1 bg-yellow-500 hover:bg-yellow-600 text-white text-xs rounded"
              >
                {{ resubmitting[billItem.id] ? 'Resubmitting...' : 'Resubmit' }}
              </button></td>
          </tr>

          <tr v-if="labItems.length">
            <td :colspan="columnCount" class="font-bold pt-3 pb-1">Laboratory</td>
          </tr>
          <tr
            v-for="(billItem, index) in labItems"
            :key="'lab-' + index"
            class="border-b border-gray-200"
          >
            <td class="py-5 pl-4 pr-3 text-gray-900 sm:pl-0">
              <div class="font-medium">{{ billItem.facility_service?.name }}</div>
            </td>
            <td class="hidden px-4 py-1 text-right text-gray-500 sm:table-cell">{{ billItem.units }}</td>
            <td class="hidden px-4 py-1 text-right text-gray-500 sm:table-cell">{{ formatCurrency(billItem.unit_price) }}</td>
            <td class="hidden px-4 py-1 text-right text-gray-500 sm:table-cell">{{ formatCurrency(billItem.total_amount) }}</td>
            <td class="hidden px-4 py-1 text-right text-gray-500 sm:table-cell">{{ billItem.paid_via || 'Not Paid' }}</td>
            <td v-if="isShaBill" class="hidden px-4 py-1 text-right text-gray-500 sm:table-cell"><span v-if="billItem.sha_intervention_info?.submitted_to_sha">{{ shaStatusBadge(billItem) }}</span>
              <button
                v-else
                @click="resubmitIntervention(billItem.id)"
                :disabled="resubmitting[billItem.id]"
                class="px-2 py-1 bg-yellow-500 hover:bg-yellow-600 text-white text-xs rounded"
              >
                {{ resubmitting[billItem.id] ? 'Resubmitting...' : 'Resubmit' }}
              </button></td>
          </tr>

          <tr v-if="radiologyItems.length">
            <td :colspan="columnCount" class="font-bold pt-3 pb-1">Radiology</td>
          </tr>
          <tr
            v-for="(billItem, index) in radiologyItems"
            :key="'rad-' + index"
            class="border-b border-gray-200"
          >
            <td class="py-5 pl-4 pr-3 text-gray-900 sm:pl-0">
              <div class="font-medium">{{ billItem.facility_service?.name }}</div>
            </td>
            <td class="hidden px-4 py-1 mt-1 text-right text-gray-500 sm:table-cell">{{ billItem.units }}</td>
            <td class="hidden px-4 py-1 mt-1 text-right text-gray-500 sm:table-cell">{{ formatCurrency(billItem.unit_price) }}</td>
            <td class="hidden px-4 py-1 mt-1 text-right text-gray-500 sm:table-cell">{{ formatCurrency(billItem.total_amount) }}</td>
            <td class="hidden px-4 py-1 text-right text-gray-500 sm:table-cell">{{ billItem.paid_via || 'Not Paid' }}</td>
            <td v-if="isShaBill" class="hidden px-4 py-1 text-right text-gray-500 sm:table-cell"><span v-if="billItem.sha_intervention_info?.submitted_to_sha">{{ shaStatusBadge(billItem) }}</span>
              <button
                v-else
                @click="resubmitIntervention(billItem.id)"
                :disabled="resubmitting[billItem.id]"
                class="px-2 py-1 bg-yellow-500 hover:bg-yellow-600 text-white text-xs rounded"
              >
                {{ resubmitting[billItem.id] ? 'Resubmitting...' : 'Resubmit' }}
              </button></td>
          </tr>

          <tr v-if="prescriptionItems.length">
            <td :colspan="columnCount" class="font-bold pt-3 pb-1">Prescription</td>
          </tr>
          <tr
            v-for="(billItem, index) in prescriptionItems"
            :key="'presc-' + index"
            class="border-b border-gray-200"
          >
            <td class="py-5 pl-4 pr-3 text-gray-900 sm:pl-0">
              <div class="font-medium">{{ billItem.inventory_item?.trade_name }}</div>
            </td>
            <td class="hidden px-4 py-1 mt-1 text-right text-gray-500 sm:table-cell">{{ billItem.units }}</td>
            <td class="hidden px-4 py-1 mt-1 text-right text-gray-500 sm:table-cell">{{ formatCurrency(billItem.unit_price) }}</td>
            <td class="hidden px-4 py-1 mt-1 text-right text-gray-500 sm:table-cell">{{ formatCurrency(billItem.total_amount) }}</td>
            <td class="hidden px-4 py-1 text-right text-gray-500 sm:table-cell">{{ billItem.paid_via || 'Not Paid' }}</td>
            <td v-if="isShaBill" class="hidden px-4 py-1 text-right text-gray-500 sm:table-cell"><span v-if="billItem.sha_intervention_info?.submitted_to_sha">{{ shaStatusBadge(billItem) }}</span>
              <button
                v-else
                @click="resubmitIntervention(billItem.id)"
                :disabled="resubmitting[billItem.id]"
                class="px-2 py-1 bg-yellow-500 hover:bg-yellow-600 text-white text-xs rounded"
              >
                {{ resubmitting[billItem.id] ? 'Resubmitting...' : 'Resubmit' }}
              </button></td>
          </tr>
        </tbody>

        <tfoot>
          <tr>
            <td colspan="2"></td>
            <td class="hidden px-4 py-5 mt-1 text-right text-gray-500 sm:table-cell font-bold">Total</td>
            <td class="hidden px-4 py-5 mt-1 text-right text-gray-500 sm:table-cell">
              {{ formatCurrency(billDetails.total_amount) }}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/api";

export default {
  name: "InvoiceTab",
  props: {
    billDetails: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showLoader: false,
      resubmitting: {},
    };
  },
  computed: {
    // ── Facility ──────────────────────────────────────────────────────────────
    // The billing controller eager-loads patient: { include: { facility: {} } }
    // so billDetails.patient.facility already contains FacilityName, logo_url,
    // County, SubCounty, NearestTown, etc. No extra API call required.
    facility() {
      return this.billDetails.patient?.facility || {};
    },
    facilityLocation() {
      const f = this.facility;
      const parts = [f.NearestTown, f.SubCounty, f.County].filter(Boolean);
      return parts.join(", ") || null;
    },

    // ── Bill type ─────────────────────────────────────────────────────────────
    isInsuranceBill() {
      const type = this.billDetails?.patient_scheme?.scheme?.scheme_type;
      return type === 1 || type === 2;
    },
    // SHA Status column only makes sense for bills actually being paid via SHA —
    // showing a "Resubmit"/status column for cash or M-TIBA bills is just noise.
    // billing_controller.rb#show exposes Bill#insurance_provider, which resolves
    // this correctly (including bills with no bill_payment_methods row yet but an
    // existing ShaVisitClaim) — the frontend can't derive this from bill_payment_methods
    // alone since payment_method_id there is polymorphic with no type discriminator.
    isShaBill() {
      return this.billDetails?.insurance_provider === 'SHA';
    },
    columnCount() {
      return this.isShaBill ? 6 : 5;
    },

    // ── Bill item groups ──────────────────────────────────────────────────────
    consultationItems() {
      return this.billDetails.bill_items?.filter(item => item.billing_category === 1) || [];
    },
    procedureItems() {
      return this.billDetails.bill_items?.filter(item => item.billing_category === 5) || [];
    },
    labItems() {
      return this.billDetails.bill_items?.filter(item => item.billing_category === 2) || [];
    },
    radiologyItems() {
      return this.billDetails.bill_items?.filter(item => item.billing_category == 3) || [];
    },
    prescriptionItems() {
      return this.billDetails.bill_items?.filter(item => item.billing_category == 4) || [];
    },

    // ── FFS ───────────────────────────────────────────────────────────────────
    ffsTotalAmount() {
      return (this.billDetails.bill_items || [])
        .filter(item => item.paid_via === 'ffs')
        .reduce((sum, item) => sum + parseFloat(item.total_amount || 0), 0);
    },
    hasFfsItems() {
      return (this.billDetails.bill_items || []).some(item => item.paid_via === 'ffs');
    },
  },
  methods: {
    formatCurrency(value) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "KES",
      }).format(value);
    },
    formatDate(dateStr) {
      if (!dateStr) return "N/A";
      return new Date(dateStr).toLocaleDateString("en-KE", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    shaStatusBadge(billItem) {
      const info = billItem.sha_intervention_info;
      if (!info || !info.is_sha_claim) {
        return 'Not Submitted';
      }
      const status = info.intervention_status;
      const code = info.intervention_code;
      if (status === 'preauth_pending') {
        return `${code} (Preauth Pending)`;
      }
      if (status === 'preauth_approved') {
        return `${code} (Preauth Approved)`;
      }
      if (status === 'active') {
        return `${code} (Submitted)`;
      }
      if (status === 'retired') {
        return `${code} (Retired)`;
      }
      return `${code} (${status})`;
    },
    async resubmitIntervention(billItemId) {
      this.resubmitting[billItemId] = true;
      try {
        const response = await apiClient.resubmitShaIntervention(billItemId);
        if (response.success) {
          this.$toast.success("Intervention resubmitted to SHA successfully");
          this.$emit('refetch');
        } else {
          this.$toast.error(response.message || "Failed to resubmit intervention");
        }
      } catch (err) {
        this.$toast.error("Network error - failed to resubmit");
        console.error(err);
      } finally {
        this.resubmitting[billItemId] = false;
      }
    },
  },
};
</script>